import { lazy, type ComponentType, type LazyExoticComponent } from "react";

/**
 * Recovery for the "Failed to fetch dynamically imported module" error — the
 * classic stale-chunk failure that happens when a code-split chunk 404s after
 * a redeploy renamed the hashed file out from under a still-open tab.
 *
 * Two pieces work together:
 *   - {@link lazyWithRetry} retries the import once (rides out a transient
 *     network blip) before letting the error bubble.
 *   - {@link reloadForFreshBuild} (called from the router's errorElement) does
 *     ONE full-page reload to pull the fresh index.html + current chunk names.
 *     The service worker uses `navigateFallback: null`, so a reload always hits
 *     the network for index.html — one reload is enough. A sessionStorage time
 *     window makes it one-shot, so a genuinely-missing chunk shows the error
 *     page instead of reload-looping.
 */

const RELOAD_TS_KEY = "chunk-reload-ts";
const RELOAD_WINDOW_MS = 10_000;

/** True for the dynamic-import / chunk-load failures we can recover from. */
export function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";
  return (
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message) ||
    /'text\/html' is not a valid javascript mime type/i.test(message)
  );
}

function reloadedRecently(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_TS_KEY) ?? "0");
    return Number.isFinite(last) && Date.now() - last < RELOAD_WINDOW_MS;
  } catch {
    return false; // sessionStorage unavailable — treat as "not recent"
  }
}

/**
 * Whether a fresh-build reload is currently allowed (we're outside the
 * loop-guard window). A pure check — safe to call during render to decide
 * between a "reloading" state and a manual fallback.
 */
export function canReloadForFreshBuild(): boolean {
  return typeof window !== "undefined" && !reloadedRecently();
}

/**
 * Reload once to pick up the freshly-deployed build. Guarded by a short
 * sessionStorage window so a persistently-missing chunk can't reload-loop.
 * Returns true when a reload was triggered, false when it declined.
 */
export function reloadForFreshBuild(): boolean {
  if (typeof window === "undefined" || reloadedRecently()) return false;
  try {
    window.sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable (rare) — reload anyway.
  }
  window.location.reload();
  return true;
}

/**
 * `React.lazy` that survives a stale chunk. Drop-in replacement for
 * `lazy(() => import(...))`: on a chunk-load error it retries the import once
 * after a short delay; if that still fails the error bubbles to the router's
 * errorElement, which reloads to the fresh build.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() =>
    factory().catch((error: unknown) => {
      if (!isChunkLoadError(error)) throw error;
      return new Promise<{ default: T }>((resolve, reject) => {
        setTimeout(() => factory().then(resolve, reject), 400);
      });
    }),
  );
}
